import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '~/components/Button';
import { ErrorPage } from '~/components/ErrorPage';
import { showToast } from '~/components/Toast';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';
import { useSublabelRequestCreate } from '~settings/api/mutations/sublabelRequestCreate';
import { CTABlock } from '~settings/components/CTABlock';
import { useModalContext } from '~settings/components/Modal';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { OPS_EMAIL } from '~settings/constants';
import type { RequestSublabelFormSchema } from '~settings/modules/sublabel-request/components/RequestSublabelForm';
import { RequestSublabelForm } from '~settings/modules/sublabel-request/components/RequestSublabelForm';
import { SublabelRequestHeader } from '~settings/modules/sublabel-request/components/SublabelRequestHeader';
import { getErrorMessage } from '~settings/modules/sublabel-request/helpers';

const FORM_ID = 'label-request-form';

export function RequestSublabelPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = useUser();
  const { confirm } = useModalContext();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics('ADA: User management');

  const canRequestLabel = hasPermission('LABEL_REQUEST.CREATE');

  const { mutate: createSublabelRequest, isPending } = useSublabelRequestCreate(
    {
      onSuccess: (data, payload) => {
        const analyticsParams = {
          label_name: payload.name,
          parent_label_name: payload.parentName ?? '',
          notes_present: !!payload.notes?.length,
          parent_pmo: data.parentPmo,
          created_at: data.createdAt,
        };

        trackEvent('sublabelCreateRequestSubmission', analyticsParams);

        navigate(SETTINGS_ROUTES.LABEL_LIST);

        showToast({
          title: t('sublabelRequest.createSuccessMessage'),
          type: 'success',
        });
      },
      onError: async (error) => {
        const message = getErrorMessage(error);

        showToast({
          title: t(message.title),
          description: t(message.description),
        });
      },
    },
  );

  const onSubmit = (data: RequestSublabelFormSchema) => {
    confirm({
      title: t('sublabelRequest.beforeSubmitDialogTitle'),
      message: (
        <Trans
          t={t}
          i18nKey="sublabelRequest.beforeSubmitDialogContent"
          components={{
            link: (
              <a href={`mailto:${OPS_EMAIL}`} className="underline">
                {OPS_EMAIL}
              </a>
            ),
          }}
        />
      ),
      confirmText: t('sublabelRequest.beforeSubmitDialogConfirmText'),
      cancelText: t('buttons.cancel'),
      onConfirm: () => {
        const payload = {
          name: data.name,
          parentId: data.parentLabel.value,
          parentName: data.parentLabel.label, // ignored by BE, needed on FE for analytics
          notes: data.notes,
        };

        createSublabelRequest(payload);
      },
    });
  };

  if (!canRequestLabel) {
    return <ErrorPage type="noAccessPage" />;
  }

  return (
    <article className="relative flex min-h-full flex-col">
      <SublabelRequestHeader />
      <div className="grow overflow-y-auto pb-xl">
        <RequestSublabelForm onSubmit={onSubmit} id={FORM_ID} />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_LIST}>{t('buttons.cancel')}</Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={FORM_ID}
          size="large"
          disabled={isPending}
        >
          {t('sublabelRequest.requestTitle')}
        </Button>
      </CTABlock>
    </article>
  );
}

RequestSublabelPage.displayName = 'RequestSublabelPage';
